/**
 * Panic LATCH (2026-09-03) - hold PANIC for a second and the rig stays dead.
 *
 * WHY, and why a one-shot panic is not enough. Panic fires CC 120/121/123 once.
 * That clears what is sounding NOW, and loses to anything that keeps
 * re-triggering: a sequencer still running upstream, Ableton re-firing a clip,
 * an LFO on a machine whose engines query 401'd so we could not enumerate it.
 * The operator's own instinct for this is to hold the button down, so that
 * gesture is what arms the sustained version.
 *
 * SEMANTICS (operator-chosen, 2026-09-03):
 *   TAP        -> one-shot panic, exactly as before. Unchanged and instant.
 *   HOLD >1s   -> LATCH: re-panic on a repeat AND suppress every outbound
 *                 dispatch, until pressed again.
 *   PRESS      -> release. Dispatch resumes. No second confirmation: getting
 *                 OUT must never be harder than getting in.
 *
 * SD-6 is preserved, not weakened. That decision says panic latency beats
 * accidental-tap cost, so the tap stays instant and unguarded; the latch is
 * strictly additional and cannot delay a tap (the one-shot fires on pointer
 * DOWN, before the hold timer can possibly have elapsed).
 *
 * WHERE THE SUPPRESSION ACTUALLY HAPPENS, and why it is one line and not
 * twenty. Every outbound path in this app - `fireAction`, the ~15 other
 * dispatch entry points, and `timeline/bridgeSync.ts`'s own senders - resolves
 * its destinations through `opbridge/store.ts`'s `resolveBridges()`. That is
 * the single shared chokepoint, so the gate lives there and everything else
 * inherits it. Gating each entry point individually is precisely the shape
 * this project keeps getting wrong: a rule honoured at one site and violated
 * at another, three recorded instances (audits/CHECKS.md). The gate DEFAULTS
 * TO BLOCKED and the handful of exceptions - the panic path itself, and pure
 * membership reads - opt out explicitly, so a new dispatch path added later is
 * suppressed by default rather than forgotten.
 *
 * KNOWN BOUNDARY, stated rather than implied: suppression covers what THIS APP
 * sends. It cannot stop a hardware controller wired straight to the rig, an
 * OPBridge rule firing server-side (the A-path binding that "fires even with
 * the tab closed"), or Ableton itself. The repeat panic is what covers those,
 * which is why the latch re-fires rather than only blocking.
 *
 * DELIBERATELY NOT PERSISTED. The state is in memory, so a reload or a tab
 * restore comes back UNLATCHED. That is the safe direction and the choice is
 * intentional: a latch restored from localStorage would greet the operator with
 * an app that silently sends nothing, in the one moment they are least able to
 * work out why - and the failure mode of forgetting to re-engage it is a rig
 * that plays, while the failure mode of a stuck persisted latch is a rig that
 * cannot. Persisting it would also make a crash mid-set unrecoverable without
 * clearing site data. If a future version does persist it, it MUST re-panic and
 * re-announce on boot rather than restoring silently.
 *
 * A reload does NOT leave the rig sounding, either: the latch's repeat has been
 * firing panic the whole time it was engaged, so whatever was stuck is already
 * stopped before the reload happens.
 */

import { createSignal } from 'solid-js';

/** How long PANIC must be held before it latches. Long enough that a normal
 *  tap never trips it, short enough to feel deliberate rather than sticky. */
export const LATCH_HOLD_MS = 1000;

/** Gap between re-panics while latched. Short enough to stamp on a sequencer
 *  that keeps re-triggering, long enough that 48 messages per bridge per
 *  repeat is not a flood - at 2s that is 24 msgs/sec/bridge, well under what
 *  the one-shot already sends in a burst. */
export const LATCH_REPEAT_MS = 2000;

const [latched, setLatched] = createSignal(false);

/**
 * Is the panic latch engaged? Read by `resolveBridges()` on EVERY dispatch, so
 * this must stay a cheap signal read with no side effects.
 *
 * Deliberately a plain function rather than a Solid resource: it is called
 * from non-reactive contexts (inside async dispatch functions) as often as
 * from reactive ones, and both must see the same value the instant it flips.
 */
export const panicLatched = latched;

export function setPanicLatched(on: boolean): void {
  setLatched(on);
}
